import SearchIcon from '@mui/icons-material/Search';
import { debounce, IconButton, InputAdornment, Paper, Stack } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useAuth } from '@shared/providers/AuthProvider';
import { useFacetHierarchy } from '@shared/queries/useFacets';
import { getSuggestions } from '@shared/services/search-service';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styles from './SearchBar.module.scss';

type SearchCategories = Record<string, string[] | undefined>;

export const getSearchParams = (
    searchQuery: string,
    searchCategories: SearchCategories
): string => {
    // Replace spaces with '+'
    const sanitizedQuery = searchQuery.replace(/\s+/g, '+');
    const terms = sanitizedQuery.split('+');
    const params: Record<string, string[]> = {
        makes: [],
        models: [],
        states: [],
        weblocationnames: [],
        keywords: [],
    };

    const existingParams = new URLSearchParams(window.location.search);

    // Add existing params to the params object
    existingParams.forEach((value, key) => {
        if (params[key]) {
            params[key].push(value);
        } else {
            params[key] = [value];
        }
    });

    const unmatchedTerms: string[] = [];

    const checkAndAddTerm = (term: string) => {
        const lowerCaseTerm = term.toLowerCase();

        const modelMatch = searchCategories.models?.find(
            model => model.toLowerCase() === lowerCaseTerm
        );
        const makeMatch = searchCategories.makes?.find(
            make => make.toLowerCase() === lowerCaseTerm
        );
        const stateMatch = searchCategories.states?.find(
            state => state.toLowerCase() === lowerCaseTerm
        );
        const locationMatch = searchCategories.weblocationnames?.find(
            location => location.toLowerCase() === lowerCaseTerm
        );

        const isTermInExistingParams = [
            'models',
            'makes',
            'states',
            'weblocationnames',
            'keywords',
        ].some(key =>
            existingParams
                .getAll(key)
                .map(v => v.toLowerCase())
                .includes(lowerCaseTerm)
        );

        const isTermInParams = ['models', 'makes', 'states', 'weblocationnames', 'keywords'].some(
            key => params[key].map(v => v.toLowerCase()).includes(lowerCaseTerm)
        );

        if (modelMatch && !isTermInParams) {
            params.models.push(modelMatch);
            return true;
        } else if (makeMatch && !isTermInParams) {
            params.makes.push(makeMatch);
            return true;
        } else if (stateMatch && !isTermInParams) {
            params.states.push(stateMatch);
            return true;
        } else if (locationMatch && !isTermInParams) {
            params.weblocationnames.push(locationMatch);
            return true;
        } else if (!isTermInExistingParams && !isTermInParams) {
            return false;
        }
        return true;
    };

    // Check if term is a model, make, state, or location
    // If not, add to unmatchedTerms
    for (let i = 0; i < terms.length; i++) {
        let term = terms[i];
        if (!checkAndAddTerm(term)) {
            if (i + 1 < terms.length) {
                term = `${terms[i]} ${terms[i + 1]}`;
                if (checkAndAddTerm(term)) {
                    i++;
                    continue;
                }
            }
            unmatchedTerms.push(terms[i]);
        }
    }

    if (unmatchedTerms.length > 0) {
        const existingKeywords = params.keywords.join(' ');
        const existingKeywordSet = new Set(existingKeywords.split(' ')); // Convert existing keywords to a Set
        const filteredNewKeywords = unmatchedTerms.filter(term => !existingKeywordSet.has(term)); // Remove duplicates

        if (filteredNewKeywords.length > 0) {
            params.keywords = [`${existingKeywords} ${filteredNewKeywords.join(' ')}`.trim()]; // Concatenate new keywords with existing keywords
        }
    }

    // Convert params object to URL query string in the specified order
    const orderedParams = ['makes', 'models', 'states', 'weblocationnames', 'keywords'];
    const queryString = orderedParams
        .filter(key => params[key].length > 0) // Remove empty categories
        .flatMap(key => params[key].map(value => `${key}=${encodeURIComponent(value)}`)) // Separate same key-value pairs
        .join('&'); // Join different params with '&'

    // Append existing params that are not in the orderedParams list
    const additionalParams = Array.from(existingParams.entries())
        .filter(([key]) => !orderedParams.includes(key))
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');

    return additionalParams ? `${queryString}&${additionalParams}` : queryString;
};

export const filterParentTerms = (
    params: Record<string, string[]>,
    parentChildMap: Record<string, Record<string, string[]>>
): Record<string, string[]> => {
    const filteredParams = { ...params };

    // Iterate over parent-child relationships in the parentChildMap
    Object.entries(parentChildMap).forEach(([parentCategory, parentToChildren]) => {
        Object.entries(parentToChildren).forEach(([parent, children]) => {
            // Check if the parent exists in the params
            if (filteredParams[parentCategory]?.includes(parent)) {
                // Check if any child exists in any category
                const childExists = Object.entries(filteredParams).some(([childCategory, values]) =>
                    children.some(child => values.includes(child))
                );

                // If a child exists, remove the parent
                if (childExists) {
                    filteredParams[parentCategory] = filteredParams[parentCategory].filter(
                        item => item !== parent
                    );
                }
            }
        });
    });

    return filteredParams;
};

type SearchBarProps = {
    logSearch?: (searchQuery: string) => void;
    isSearchPage?: boolean;
    searchPath?: string; // New prop for search path
};

const SearchBar = ({ logSearch, isSearchPage = false, searchPath = 'search' }: SearchBarProps) => {
    const { isAuthenticated, getToken } = useAuth();
    const [open, setOpen] = useState(false);
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const navigate = useNavigate();
    const searchInputRef = useRef<HTMLInputElement>();
    const { parentChildMap, flatStructure } = useFacetHierarchy([
        ['makes', 'models'],
        ['states', 'weblocationnames'],
    ]);

    const handleAutoComplete = useCallback(
        (text: string) => {
            if (!text || text == '') return;

            getSuggestions(text, getToken).then((suggestions: string[]) => {
                if (suggestions && suggestions.length > 0) {
                    setSuggestions(suggestions);
                    setOpen(true);
                } else {
                    setOpen(false);
                }
            });
        },
        [getToken]
    );

    const triggerSearch = (searchTerm: string) => {
        if (!searchTerm || searchTerm == '') return;

        const searchCategories: SearchCategories = {
            makes: flatStructure?.makes,
            models: flatStructure?.models,
            states: flatStructure?.states,
            weblocationnames: flatStructure?.weblocationnames,
        };

        // Get the initial search parameters
        const searchParams = getSearchParams(searchTerm, searchCategories);

        // Convert the query string back to an object for filtering
        const paramsObject = Array.from(new URLSearchParams(searchParams).entries()).reduce(
            (acc, [key, value]) => {
                if (!acc[key]) {
                    acc[key] = [];
                }
                acc[key].push(value);
                return acc;
            },
            {} as Record<string, string[]>
        );

        // Filter out parent terms using the parentChildMap
        const filteredParams = filterParentTerms(paramsObject, parentChildMap);

        // Convert the filtered params back to a query string
        const orderedParams = ['makes', 'models', 'states', 'weblocationnames', 'keywords'];
        const queryString = orderedParams
            .filter(key => filteredParams[key]?.length > 0) // Remove empty categories
            .flatMap(key => filteredParams[key].map(value => `${key}=${encodeURIComponent(value)}`)) // Separate same key-value pairs
            .join('&');

        if (logSearch) {
            logSearch(searchTerm);
        }

        navigate(`/${searchPath}?${queryString}`);
    };

    const handleButtonClick = () => {
        const { value } = searchInputRef.current;
        triggerSearch(value);
    };

    const handleOnKeyDown = e => {
        // prevents event bubbling up to onChange event, causing multiple triggerSearch calls
        e.stopPropagation();
        if (e.keyCode == 13 || e.code === 'Enter') {
            triggerSearch(e.target.value);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleOnChange = useCallback(
        debounce((text: string) => {
            handleAutoComplete(text);
        }, 400),
        []
    );

    const handleClear = () => {
        if (isSearchPage) {
            const existingParams = new URLSearchParams(window.location.search);
            existingParams.delete('keywords');
            navigate(`/${searchPath}?${existingParams.toString()}`);
        }
    };

    if (!isAuthenticated()) {
        // User isn't authenticated. Don't render.
        return <></>;
    }

    return (
        <>
            <Stack spacing={2} className={styles['search-container']}>
                <Autocomplete
                    value={''}
                    options={suggestions}
                    open={open}
                    freeSolo
                    noOptionsText={'No suggestions'}
                    onChange={(_event, value, reason) => {
                        if (reason.toLowerCase() === 'clear' || !value) {
                            handleClear();
                            setOpen(false);
                            return false;
                        }

                        triggerSearch(value);
                        return false;
                    }}
                    onKeyDown={handleOnKeyDown}
                    onOpen={() => {
                        setOpen(true);
                    }}
                    onClose={() => {
                        setOpen(false);
                    }}
                    renderInput={params => (
                        <TextField
                            {...params}
                            placeholder="Search by make, model, or keyword"
                            inputRef={searchInputRef}
                            onKeyDown={handleOnKeyDown}
                            onChange={e => handleOnChange(e.target.value)}
                            variant="outlined"
                            sx={{
                                '& fieldset': { border: 'none' },
                                padding: '0px',
                            }}
                            inputProps={{
                                ...params.inputProps,
                                maxLength: 1500,
                            }}
                            InputProps={{
                                ...params.InputProps,
                                style: { padding: 'unset' },
                                endAdornment: (
                                    <>
                                        <InputAdornment
                                            position="end"
                                            style={{ position: 'relative', right: '-12px' }}>
                                            {params.InputProps.endAdornment}
                                        </InputAdornment>
                                        <InputAdornment position="end" style={{ paddingRight: 12 }}>
                                            <IconButton edge="end" onClick={handleButtonClick}>
                                                <SearchIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    </>
                                ),
                            }}
                        />
                    )}
                    PaperComponent={({ children }) => (
                        <Paper
                            sx={{
                                '& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused': {
                                    bgcolor: '#f2faff',
                                },
                                '& .MuiAutocomplete-listbox .MuiAutocomplete-option:hover': {
                                    bgcolor: '#f0f1f2',
                                },
                            }}>
                            {children}
                        </Paper>
                    )}
                />
            </Stack>
        </>
    );
};

export default SearchBar;
